<!--*
 * Screen with the site details
 *
 * @author   mauricio.figueroa
 * @date     2020-07-03
 *
 * @copyright 2020 Smartmatic Corp. All Rights Reserved.
 *
 * @license
 *
 * Confidential information of Smartmatic Labs. Not for disclosure or
 * distribution prior written consent. This software contains code, techniques
 * and know-how which is confidential and proprietary to Smartmatic.
 *
 * Use of this software is subject to the terms of an end user license
 * agreement.
-->
<template>
  <pull-to-refresh>
    <div id="background" class="wrapper">
      <SubMenuHeader
        :title="$t('siteDetails.pageTitle')"
        @onGoBack="back"
        @showVideo="$emit('showVideo')"
      />
      <div class="container content">
        <div class="row">
          <div class="col--12">
            <smtt-contextual-box
              ref="userErrorBox"
              is-visible="false"
              :message-title="errorMessage"
              variation="error"
              dismissable="true"
            ></smtt-contextual-box>
          </div>
        </div>
        <div v-if="noAssignment" class="full-screen card m-6">
          <div
            class="d-flex flex-column justify-content-center align-items-center"
          >
            <div class="h3">{{ $t("reassignment.site.error_title") }}</div>
            <div>{{ $t("reassignment.site.error_subtitle") }}</div>
          </div>
        </div>
        <div v-else>
          <Details
            :site-name="info.siteName"
            :image="image()"
            :site-directions="info.siteFullAddress"
            :site-phone="info.sitePhone"
            :site-id="info.siteId"
            :site-rooms="info.siteRooms"
            :site-code="info.siteCode"
            :open-hour="info.siteOoperatingTimeOpen"
            :close-hour="info.siteOperatingTimeClose"
            :site-full-address="info.siteFullAddress"
            :site-contact-name="info.siteContactName"
            :latitude="info.latitude"
            :longitude="info.longitude"
            :phase-name="phaseName"
            :transition-comment="transitionComment"
            :deadline="info.deadline"
            :site-location-not-found="siteLocationNotFound"
            :has-workflow="hasWorkflow"
          />
          <div class="after-details mb-5">
            <div class="h2 title">
              {{ $t("siteDetails.alreadyThere.title") }}
            </div>
            <div>
              {{ $t("siteDetails.alreadyThere.subtitle") }}
            </div>
            <PlacesList
              class="mt-5"
              :places="places"
              :survey-enabled="isSurveyEnabled"
              visibility-mode="pending"
            >
              <div class="col--lg--4 col--md--6 col--12 my-4">
                <AddRoom
                  v-if="info.hasNormalSurveys && checkPermission('Add Room')"
                  :disabled="info.outOfTime"
                  :surveyTemplates="normalSurveyTemplates"
                  @inputChanged="inputChangedHandler"
                  @newRoomAdded="roomAddedHandler"
                />
              </div>
            </PlacesList>
            <div v-if="info.hasRecurrentSurveys" class="mt-5">
              <b>{{ $t("siteDetails.alreadyThere.places.recurrent") }}</b>
              <PlacesList
                :places="places"
                :survey-enabled="isSurveyEnabled"
                visibility-mode="recurrent"
              >
                <div class="col--lg--4 col--md--6 col--12 my-4">
                  <AddRoom
                    v-if="checkPermission('Add Room')"
                    :disabled="info.outOfTime"
                    :recurrent="true"
                    :surveyTemplates="recurrentSurveyTemplates"
                    @inputChanged="inputChangedHandler"
                    @newRoomAdded="roomAddedHandler"
                  />
                </div>
              </PlacesList>
            </div>
            <div v-if="thereAreCompletedRooms" class="mt-5">
              <b>{{ $t("siteDetails.alreadyThere.places.completed") }}</b>
              <PlacesList
                :places="places"
                :survey-enabled="isSurveyEnabled"
                visibility-mode="completed"
              />
            </div>
          </div>
        </div>
      </div>
      <smtt-toast
        ref="statusErrorToast"
        id="errorToast"
        style="border: none"
      ></smtt-toast>
    </div>
  </pull-to-refresh>
</template>

<script>
import { mapActions } from "vuex";

import store from "../../store/store";
import SubMenuHeader from "../SubMenuHeader";

import Details from "./Details";
import PlacesList from "./PlacesList";
import AddRoom from "./AddRoom";
import PullToRefresh from "../PullToRefresh.vue";

export default {
  name: "SiteDetailsScreen",
  components: {
    AddRoom,
    PlacesList,
    SubMenuHeader,
    Details,
    PullToRefresh,
  },
  data: function () {
    return {
      errorMessage: null,
      info: {},
      places: {},
      isSurveyEnabled: true,
      transitionComment: null,
      phaseName: null,
      normalSurveyTemplates: [],
      recurrentSurveyTemplates: [],
      noAssignment: false,
    };
  },
  computed: {
    thereAreCompletedRooms() {
      if (this.places.rooms != null) {
        return (
          this.places.rooms.filter((pl) => {
            if (pl.roomWorkflowPhaseName !== null) {
              return pl.roomWorkflowPhaseName === "Finished";
            } else {
              return pl.surveyStatus === 2;
            }
          }).length > 0
        );
      }
      return false;
    },
    siteLocationNotFound: function () {
      return this.$store.state.siteLocations.find((siteLoc) => {
        return siteLoc.siteId === this.info.siteId;
      })?.notFound;
    },
    hasWorkflow() {
      let hasWorkflow = false;

      if (this.places.rooms != null) {
        let filteredRooms = this.places.rooms.filter(
          (place) => place.roomWorkflowPhaseName !== null
        );

        if (filteredRooms.length > 0) {
          hasWorkflow = true;
        }
      }

      return hasWorkflow;
    },
  },
  methods: {
    ...mapActions([
      "getSiteDetails",
      "getRooms",
      "getTransitionOptions",
      "getSurveysDataBySite",
    ]),
    back: function () {
      this.$router.back();
    },
    image: function () {
      if (this.info.sitePicture) {
        return this.info.sitePicture;
      } else {
        return null;
      }
    },
    checkPermission(permission) {
      return store.getters.UserHasPermission(permission);
    },
    inputChangedHandler(hideActions) {
      hideActions
        ? (this.isSurveyEnabled = false)
        : (this.isSurveyEnabled = true);
    },
    roomAddedHandler(room) {
      this.places = room;
      this.info.siteRooms.pending += 1;
      this.info.siteRooms.total += 1;
      localStorage.setItem("places", JSON.stringify(this.places.rooms));
    },
    showError(msg) {
      this.$refs.userErrorBox.isVisible = true;
      this.errorMessage = msg?.code ? this.$t(msg.code) : msg;
    },
    catchAction(error) {
      const toastElement = this.$refs.statusErrorToast;
      toastElement.error({
        message:
          error.response != null
            ? error.response.data?.message
            : this.$t("error.default.connection.message"),
        header: this.$t("error.default.connection.title"),
        duration: 5000,
      });
    },
    retrieveTransitionOptions() {
      this.getTransitionOptions({ surveyId: this.info.surveyId })
        .then((responseT) => {
          if (responseT.success) {
            this.transitionComment = responseT.comment;
            this.phaseName = responseT.phaseName;
          } else {
            this.showError(responseT.message);
          }
        })
        .catch((error) => {
          this.catchAction(error);
        });
    },
  },
  async created() {
    try {
      const responseSiteDetails = await this.getSiteDetails({
        assignmentId: this.$route.params.assignmentId,
      });
      if (responseSiteDetails.success) {
        this.info = responseSiteDetails;
        this.isSurveyEnabled = responseSiteDetails.userHasRole;
        this.retrieveTransitionOptions();
        const responseSurveyData = await this.getSurveysDataBySite({
          siteId: this.info.siteId,
        });
        if (responseSurveyData.success) {
          for (let i = 0; i < responseSurveyData.data.length; i++) {
            if (responseSurveyData.data[i].isRecurrent) {
              // this.$set(this.recurrentSurveyTemplates, i, responseSurveyData.data[i]);
              this.recurrentSurveyTemplates.push(responseSurveyData.data[i]);
            } else {
              // this.$set(this.normalSurveyTemplates, i, responseSurveyData.data[i]);
              this.normalSurveyTemplates.push(responseSurveyData.data[i]);
            }
          }
        } else {
          this.showError(responseSurveyData.message);
        }
      } else {
        if (responseSiteDetails?.cause === "reassignment") {
          this.noAssignment = true;
          return;
        } else {
          this.showError(responseSiteDetails.message);
        }
      }
    } catch (error) {
      this.catchAction(error);
    }
    try {
      const responseRooms = await this.getRooms({
        assignmentId: this.$route.params.assignmentId,
      });
      if (responseRooms.success) {
        this.places = responseRooms;
        localStorage.setItem("places", JSON.stringify(this.places.rooms));
      } else {
        this.showError(responseRooms.message);
      }
    } catch (error) {
      this.catchAction(error);
    }
  },
};
</script>

<style scoped>
#background {
  background-color: #fafafa;
  min-height: 100vh;
}
.after-details {
  padding: 40px 0 0;
}
.title {
  margin: 0 0 9px 0;
}

.full-screen {
  display: grid;
}

.wrapper {
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100svh;
}
.content {
  display: grid;
  grid-template-rows: auto 1fr;
}
</style>
